/**
 * LandingPreloader - Branded Preloader for Landing Page
 * 
 * Modern fintech-style loading experience with:
 * - Animated Supabank logo with enhanced glow
 * - Progress bar with shimmer effect
 * - Rotating status messages
 * - Blur→sharp content reveal transition
 * 
 * Design Philosophy:
 * - No boring skeletons for 2025-2026
 * - Branded experience that reinforces tech credibility
 * - Shows "real work being done" with progress messages
 * 
 * @see CANON.md - Design tokens and motion system
 */

import { motion, AnimatePresence } from 'framer-motion';
import React, { useEffect, useState, useCallback } from 'react';
import { SupabankLogo, BRAND_COLORS } from '@/components/brand/SupabankLogo';
import { cn } from '@/lib/utils';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { appleSprings } from '@/ui/tokens/apple-springs';

interface LandingPreloaderProps {
  /** Duration in ms before auto-completing (default: 2500) */
  duration?: number;
  /** Callback when loading completes */
  onComplete?: () => void;
  /** Optional className */
  className?: string;
}

// Rotating status messages - fintech/crypto themed
const STATUS_MESSAGES = [
  'Initializing secure connection...',
  'Loading 12 blockchain networks...',
  'Syncing 180+ country rails...',
  'Connecting payment infrastructure...',
  'Almost ready...',
] as const;

// Message rotation interval (ms) - slower for readability
const MESSAGE_INTERVAL = 1000;

/**
 * Animated Progress Bar with shimmer effect
 */
function ProgressBar({ progress }: { progress: number }) {
  return (
    <div className="w-64 h-1 bg-slate-200/20 dark:bg-slate-700/30 rounded-full overflow-hidden">
      <motion.div
        className="h-full rounded-full relative"
        style={{
          background: `linear-gradient(90deg, ${BRAND_COLORS.accent}, ${BRAND_COLORS.cryptoMint})`,
        }}
        initial={{ width: '0%' }}
        animate={{ width: `${progress}%` }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        {/* Shimmer overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
          animate={{ x: ['-100%', '200%'] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      </motion.div>
    </div>
  );
}

/**
 * Animated status message with fade transition
 */
function StatusMessage({ message }: { message: string }) {
  return (
    <AnimatePresence mode="wait">
      <motion.p
        key={message}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.24, ease: 'easeOut' }}
        className="text-sm text-muted-foreground/70 font-mono tracking-tight"
      >
        {message}
      </motion.p>
    </AnimatePresence>
  );
}

/**
 * Enhanced Logo with extra glow effect for preloader
 */
function EnhancedLogo({ isAnimating }: { isAnimating: boolean }) {
  return (
    <motion.div
      className="relative"
      animate={isAnimating ? {
        scale: [1, 1.02, 1],
      } : {}}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      {/* Background glow */}
      <motion.div
        className="absolute inset-0 -m-8 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${BRAND_COLORS.accent}30, transparent 70%)`,
        }}
        animate={isAnimating ? {
          opacity: [0.3, 0.6, 0.3],
          scale: [0.9, 1.1, 0.9],
        } : { opacity: 0.3 }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      
      {/* Logo */}
      <SupabankLogo
        variant="full"
        size="lg"
        living={true}
        className="relative z-10"
      />
    </motion.div>
  );
}

/**
 * Main LandingPreloader Component
 */
export function LandingPreloader({
  duration = 2500,
  onComplete,
  className,
}: LandingPreloaderProps) {
  const prefersReducedMotion = useReducedMotion();
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Progress simulation - accelerates towards end
  useEffect(() => {
    if (prefersReducedMotion) {
      setProgress(100);
      setIsComplete(true);
      onComplete?.();
      return;
    }

    const startTime = Date.now();
    const updateInterval = 50;

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const rawProgress = (elapsed / duration) * 100;
      
      // Ease-out curve for more realistic loading feel
      const easedProgress = Math.min(100, rawProgress * (2 - rawProgress / 100));
      
      setProgress(easedProgress);

      if (elapsed >= duration) {
        clearInterval(timer);
        setProgress(100);
        
        // Small delay before completing
        setTimeout(() => {
          setIsComplete(true);
          onComplete?.();
        }, 200);
      }
    }, updateInterval);

    return () => clearInterval(timer);
  }, [duration, onComplete, prefersReducedMotion]);

  // Message rotation
  useEffect(() => {
    if (prefersReducedMotion || isComplete) return;

    const timer = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % STATUS_MESSAGES.length);
    }, MESSAGE_INTERVAL);

    return () => clearInterval(timer);
  }, [prefersReducedMotion, isComplete]);

  // Determine current message based on progress
  const currentMessage = isComplete 
    ? 'Ready.' 
    : STATUS_MESSAGES[messageIndex];

  return (
    <motion.div
      className={cn(
        'fixed inset-0 z-50 flex flex-col items-center justify-center',
        'bg-background/95 backdrop-blur-sm',
        className
      )}
      initial={{ opacity: 1 }}
      animate={{ opacity: isComplete ? 0 : 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Loading Supabank"
    >
      <div className="flex flex-col items-center gap-8">
        {/* Animated Logo */}
        <EnhancedLogo isAnimating={!prefersReducedMotion && !isComplete} />

        {/* Progress Section */}
        <div className="flex flex-col items-center gap-4">
          {/* Progress Bar */}
          {!prefersReducedMotion && (
            <ProgressBar progress={progress} />
          )}

          {/* Progress Percentage */}
          <motion.span
            className="text-xs font-mono text-muted-foreground/50 tabular-nums"
            animate={{ opacity: isComplete ? 0 : 1 }}
          >
            {Math.round(progress)}%
          </motion.span>

          {/* Status Message */}
          <StatusMessage message={currentMessage} />
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Content Reveal Wrapper - blur→sharp transition
 * Wraps content to animate from blurred to sharp when preloader completes
 */
export function ContentReveal({
  isReady,
  children,
  className,
}: {
  isReady: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, filter: 'blur(10px)', scale: 0.98 }}
      animate={isReady ? {
        opacity: 1,
        filter: 'blur(0px)',
        scale: 1,
      } : {
        opacity: 0,
        filter: 'blur(10px)',
        scale: 0.98,
      }}
      transition={{
        duration: 0.6,
        ease: [0.25, 1, 0.5, 1], // Standard easing from tailwind config
      }}
    >
      {children}
    </motion.div>
  );
}

export default LandingPreloader;
